import * as Dialog from '@radix-ui/react-dialog';
import { useRef } from 'react';

import { Button } from '@/components/Button/Button';

import styles from './DiscardCoachChangesDialog.module.css';

interface DiscardCoachChangesDialogProps {
  onDiscard: () => void;
  // Also called for Escape and a click outside: both mean "not yet".
  onKeepEditing: () => void;
  open: boolean;
}

// Asked before unsaved coach edits are thrown away: by Cancel, or by leaving
// the page. The repo's confirmation shape (see ResetConversationDialog), given
// the alertdialog role because it interrupts to ask about losing work. Focus
// starts on the safe choice, and goes back to whatever opened the dialog when
// it closes: Radix only returns it to a Dialog.Trigger, and this dialog has
// none (Cancel and a held link both open it).
export function DiscardCoachChangesDialog({
  onDiscard,
  onKeepEditing,
  open,
}: DiscardCoachChangesDialogProps) {
  const keepEditingRef = useRef<HTMLElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  return (
    <Dialog.Root
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onKeepEditing();
        }
      }}
      open={open}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          className={styles.content}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            // Gone after a discard: the page decides where focus goes then.
            if (returnFocusRef.current?.isConnected) {
              returnFocusRef.current.focus();
            }
          }}
          onOpenAutoFocus={(event) => {
            // Focus has not moved yet: this is what opened the dialog.
            returnFocusRef.current =
              document.activeElement instanceof HTMLElement ? document.activeElement : null;
            event.preventDefault();
            keepEditingRef.current?.focus();
          }}
          role="alertdialog"
        >
          <div>
            <Dialog.Title className={styles.title}>Discard your changes?</Dialog.Title>
            <Dialog.Description className={styles.description}>
              Your changes to this coach haven’t been saved. If you discard them, the coach
              stays as you last saved it.
            </Dialog.Description>
          </div>

          <div className={styles.footer}>
            <Button onClick={onKeepEditing} ref={keepEditingRef} variant="secondary">
              Keep editing
            </Button>
            <Button destructive onClick={onDiscard}>
              Discard changes
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
